// The format settings sheet's model (SuiteCompile/PresetText.swift and FormatSettings.swift; design-docs/Format settings —
// design.md): a project's own version of a format, made and changed a setting at a time, stored as an ordinary preset that
// inherits the built-in, written as block YAML a setting a line in a fixed order. The same text as the Swift writes, pinned
// by `fixtures/format-settings/results.json` (written by `FormatSettingsTests`).
import { native, PresetLibrary, swiftDoubleDescription, type PMap, type PValue, loadYAML } from './presets.ts';
import { numberWords, roman } from './manuscript.ts';
import type { CompilePreset } from './types.ts';
import type { PublishFormat } from './publish.ts';

// MARK: PresetText

const KEY_ORDER = [
  'name', 'description', 'inherits', 'format', 'blind', 'journal', 'edition', 'select', 'map', 'render',
  'arrangement', 'respectIncludeInCompile', 'frontMatter', 'backMatter', 'exclude', 'include', 'document', 'postMatter',
  'reversed', 'onePost',
  'collection', 'field', 'equals', 'sectionType', 'hasChildren', 'hasSubfolders', 'depth', 'kind', 'post', 'inPost',
  'rules', 'when', 'role', 'join',
  'page', 'font', 'lineSpacing', 'firstLineIndent', 'paragraphSpacing', 'justify', 'sceneBreak', 'header', 'titlePage',
  'partHeading', 'chapterHeading', 'chapterStartsNewPage', 'chapterDrop', 'endMarker', 'italicAsUnderline', 'keepHighlights', 'print',
  'documentTitle', 'letterhead', 'audio', 'screenplay', 'links',
  'family', 'size', 'margins', 'inside', 'outside', 'top', 'bottom',
  'contact', 'wordCount', 'wordCountRounding', 'byline',
  'versoHead', 'rectoHead', 'chapterOpensRecto', 'pageEdgeOrnament', 'platform', 'bleed', 'signature', 'preventWidows',
  'headingScale', 'balanceSpreads', 'pdfx',
];
const RANK = new Map<string, number>();
KEY_ORDER.forEach((k, i) => { if (!RANK.has(k)) RANK.set(k, i); });

const codePoints = (s: string) => Array.from(s, (c) => c.codePointAt(0)!);
function byScalars(a: string, b: string): number {
  const x = codePoints(a), y = codePoints(b);
  for (let i = 0; i < Math.min(x.length, y.length); i++) if (x[i] !== y[i]) return x[i] - y[i];
  return x.length - y.length;
}

function ordered(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const x = RANK.get(a), y = RANK.get(b);
    if (x !== undefined && y !== undefined) return x - y;
    if (x !== undefined) return -1;
    if (y !== undefined) return 1;
    return byScalars(a, b);
  });
}

const isMap = (v: PValue | undefined): v is PMap => typeof v === 'object' && v !== null && !Array.isArray(v);
const RESERVED = new Set(['y', 'n', 'yes', 'no', 'on', 'off', 'true', 'false', 'null', 'nil']);

function plain(s: string): boolean {
  if (!/^[A-Za-z]/.test(s) || s.endsWith(' ') || RESERVED.has(s.toLowerCase())) return false;
  return /^[A-Za-z0-9 ._()'/-]*$/.test(s);
}
const plainKey = (s: string) => /^[A-Za-z_][A-Za-z0-9_-]*$/.test(s);

function quoted(s: string): string {
  let out = '"';
  for (const c of s) {
    const v = c.codePointAt(0)!;
    if (c === '\\') out += '\\\\';
    else if (c === '"') out += '\\"';
    else if (c === '\n') out += '\\n';
    else if (c === '\t') out += '\\t';
    else if (c === '\r') out += '\\r';
    else if (v < 0x20 || v === 0x7f) out += '\\x' + v.toString(16).toUpperCase().padStart(2, '0');
    else out += c;
  }
  return out + '"';
}

/** A whole number without a point; otherwise as Swift's `Double.description` writes it. */
function number(d: number): string {
  if (Number.isInteger(d) && Math.abs(d) < 1e15) return String(d === 0 ? 0 : d);
  return swiftDoubleDescription(d);
}

/** A value as the text writes it. */
export function presetScalar(v: PValue): string {
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return number(v);
  if (typeof v === 'string') return plain(v) ? v : quoted(v);
  if (Array.isArray(v)) return '[' + v.map(presetScalar).join(', ') + ']';
  const keys = ordered(Object.keys(v));
  return keys.length ? '{' + keys.map((k) => `${k}: ${presetScalar(v[k])}`).join(', ') + '}' : '{}';
}

function lines(map: PMap, indent: number): string[] {
  const pad = ' '.repeat(indent);
  const out: string[] = [];
  for (const key of ordered(Object.keys(map))) {
    const name = plainKey(key) ? key : quoted(key);
    const v = map[key];
    if (isMap(v)) {
      if (!Object.keys(v).length) out.push(`${pad}${name}: {}`);
      else out.push(`${pad}${name}:`, ...lines(v, indent + 2));
    } else if (Array.isArray(v)) {
      if (!v.length) out.push(`${pad}${name}: []`);
      else out.push(`${pad}${name}:`, ...items(v, indent + 2));
    } else {
      out.push(`${pad}${name}: ${presetScalar(v)}`);
    }
  }
  return out;
}

function items(list: PValue[], indent: number): string[] {
  const pad = ' '.repeat(indent);
  const out: string[] = [];
  for (const item of list) {
    if (isMap(item)) {
      if (!Object.keys(item).length) { out.push(`${pad}- {}`); continue; }
      const inner = lines(item, indent + 2);
      inner[0] = `${pad}- ${inner[0].slice(indent + 2)}`;
      out.push(...inner);
    } else if (Array.isArray(item)) {
      out.push(`${pad}- [${item.map(presetScalar).join(', ')}]`);
    } else {
      out.push(`${pad}- ${presetScalar(item)}`);
    }
  }
  return out;
}

/** `PresetText.write`: block YAML, two spaces a level, every line ending in a newline. */
export function writePreset(tree: PMap): string {
  return lines(tree, 0).map((l) => l + '\n').join('');
}

/** `PresetText.same`: whether two values are the same as written (1 and 1.0 are). */
export function samePresetValue(a: PValue | undefined, b: PValue | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return presetScalar(a) === presetScalar(b);
}

/** `PresetText.hasComments`: whether writing the text again would drop a comment. */
export function presetHasComments(yaml: string): boolean {
  for (const line of yaml.split('\n')) {
    let quote: string | null = null;
    let previous = ' ';
    for (const c of line) {
      if (quote) {
        if (c === quote && !(quote === '"' && previous === '\\')) quote = null;
      } else if (c === '"' || c === "'") {
        quote = c;
      } else if (c === '#' && (previous === ' ' || previous === '\t')) {
        return true;
      }
      previous = c;
    }
  }
  return false;
}

export function valueAt(tree: PMap, path: readonly string[]): PValue | undefined {
  let v: PValue | undefined = tree;
  for (const key of path) {
    if (!isMap(v)) return undefined;
    v = v[key];
  }
  return v;
}

function setting(map: PMap, path: readonly string[], value: PValue | undefined): PMap {
  const [key, ...rest] = path;
  const out: PMap = { ...map };
  if (!rest.length) {
    if (value === undefined) delete out[key];
    else out[key] = value;
    return out;
  }
  const child = setting(isMap(map[key]) ? (map[key] as PMap) : {}, rest, value);
  if (Object.keys(child).length) out[key] = child;
  else delete out[key];
  return out;
}

/** `PresetText`: a preset's text as a tree, set a key at a time. */
export class PresetText {
  constructor(readonly tree: PMap = {}) {}

  static read(yaml: string): PresetText {
    if (!yaml.trim()) return new PresetText();
    const loaded = native(loadYAML(yaml));
    if (loaded === null) return new PresetText();
    if (!isMap(loaded)) throw new Error('not a YAML mapping');
    return new PresetText(loaded);
  }

  value(path: readonly string[]): PValue | undefined { return valueAt(this.tree, path); }
  set(value: PValue | undefined, path: readonly string[]): PresetText { return new PresetText(setting(this.tree, path, value)); }
  get yaml(): string { return writePreset(this.tree); }
}

// MARK: FormatSetting

export const FORMAT_SETTINGS = [
  'frontMatter', 'backMatter', 'respectIncludeInCompile', 'excludedStatuses', 'onlyCollection',
  'pageSize', 'margins',
  'font', 'fontSize', 'lineSpacing', 'paragraphs', 'justify', 'italicAsUnderline', 'keepHighlights',
  'chapterHeading', 'partHeading', 'chapterStartsNewPage', 'chapterOpensRecto', 'chapterDrop',
  'sceneBreak',
  'titlePage', 'titlePageContact', 'titlePageWordCount', 'titlePageByline', 'header', 'endMarker',
  'versoHead', 'rectoHead', 'pageEdgeOrnament', 'preventWidows', 'balanceSpreads',
] as const;
export type FormatSetting = (typeof FORMAT_SETTINGS)[number];

/** The preset keys a setting writes (`FormatSetting.paths`). */
export function settingPaths(s: FormatSetting): string[][] {
  switch (s) {
    case 'frontMatter': return [['select', 'frontMatter']];
    case 'backMatter': return [['select', 'backMatter']];
    case 'respectIncludeInCompile': return [['select', 'respectIncludeInCompile']];
    case 'excludedStatuses': return [['select', 'exclude']];
    case 'onlyCollection': return [['select', 'include']];
    case 'pageSize': return [['render', 'page', 'size']];
    case 'margins': return ['margins', 'inside', 'outside', 'top', 'bottom'].map((k) => ['render', 'page', k]);
    case 'font': return [['render', 'font', 'family']];
    case 'fontSize': return [['render', 'font', 'size']];
    case 'lineSpacing': return [['render', 'lineSpacing']];
    case 'paragraphs': return [['render', 'firstLineIndent'], ['render', 'paragraphSpacing']];
    case 'justify': return [['render', 'justify']];
    case 'italicAsUnderline': return [['render', 'italicAsUnderline']];
    case 'keepHighlights': return [['render', 'keepHighlights']];
    case 'chapterHeading': return [['render', 'chapterHeading']];
    case 'partHeading': return [['render', 'partHeading']];
    case 'chapterStartsNewPage': return [['render', 'chapterStartsNewPage']];
    case 'chapterOpensRecto': return [['render', 'print', 'chapterOpensRecto']];
    case 'chapterDrop': return [['render', 'chapterDrop']];
    case 'sceneBreak': return [['render', 'sceneBreak']];
    case 'titlePage': return [['render', 'titlePage', 'include']];
    case 'titlePageContact': return [['render', 'titlePage', 'contact']];
    case 'titlePageWordCount': return [['render', 'titlePage', 'wordCount']];
    case 'titlePageByline': return [['render', 'titlePage', 'byline']];
    case 'header': return [['render', 'header']];
    case 'endMarker': return [['render', 'endMarker']];
    case 'versoHead': return [['render', 'print', 'versoHead']];
    case 'rectoHead': return [['render', 'print', 'rectoHead']];
    case 'pageEdgeOrnament': return [['render', 'print', 'pageEdgeOrnament']];
    case 'preventWidows': return [['render', 'print', 'preventWidows']];
    case 'balanceSpreads': return [['render', 'print', 'balanceSpreads']];
  }
}

// MARK: FormatSettings (the sections and choices)

export type FormatSection = 'contents' | 'page' | 'text' | 'chapters' | 'sceneBreaks' | 'titlePage' | 'print';

export const SECTION_TITLES: Readonly<Record<FormatSection, string>> = {
  contents: "What's in it", page: 'Page', text: 'Text', chapters: 'Chapters', sceneBreaks: 'Scene breaks',
  titlePage: 'Title page and header', print: 'Print',
};

/** `FormatSettings.isAdjustable`: books and manuscripts, not scripts, audio, a single document or a journal's one post. */
export function isAdjustable(p: CompilePreset): boolean {
  if (p.render.screenplay || p.render.audio || p.format === 'audiobook' || p.select.document !== undefined || p.select.onePost === true) return false;
  return ['docx', 'pdf', 'epub', 'markdown', 'text'].includes(p.format);
}

/** `FormatSettings.sections(for:)`. */
export function formatSections(p: CompilePreset): { section: FormatSection; settings: FormatSetting[] }[] {
  if (!isAdjustable(p)) return [];
  const print = !!p.render.print;
  const out: { section: FormatSection; settings: FormatSetting[] }[] = [
    { section: 'contents', settings: ['frontMatter', 'backMatter', 'respectIncludeInCompile', 'excludedStatuses', 'onlyCollection'] },
  ];
  if (p.format === 'docx' || p.format === 'pdf') {
    out.push({ section: 'page', settings: ['pageSize', 'margins'] });
    // Only a Word file can carry the writer's highlights and text colours (an editor's copy).
    out.push({ section: 'text', settings: ['font', 'fontSize', 'lineSpacing', 'paragraphs', 'justify', ...(print ? [] : ['italicAsUnderline' as const]),
      ...(p.format === 'docx' ? ['keepHighlights' as const] : [])] });
    out.push({ section: 'chapters', settings: ['chapterHeading', 'partHeading', 'chapterStartsNewPage', ...(print ? ['chapterOpensRecto' as const] : []), 'chapterDrop'] });
    out.push({ section: 'sceneBreaks', settings: ['sceneBreak'] });
    if (print) out.push({ section: 'print', settings: ['versoHead', 'rectoHead', 'pageEdgeOrnament', 'preventWidows', 'balanceSpreads'] });
    else out.push({ section: 'titlePage', settings: ['titlePage', 'titlePageContact', 'titlePageWordCount', 'titlePageByline', 'header', 'endMarker'] });
  } else if (p.format === 'epub') {
    out.push({ section: 'text', settings: ['paragraphs'] });
    out.push({ section: 'chapters', settings: ['chapterHeading', 'partHeading'] });
    out.push({ section: 'sceneBreaks', settings: ['sceneBreak'] });
  } else {
    out.push({ section: 'chapters', settings: ['chapterHeading', 'partHeading'] });
    out.push({ section: 'sceneBreaks', settings: ['sceneBreak'] });
  }
  return out;
}

export interface Choice<V> { value: V; label: string }

export const pageSizes = (print: boolean): Choice<string>[] => print
  ? [{ value: '5x8', label: '5 × 8 in' }, { value: '5.25x8', label: '5.25 × 8 in' }, { value: '5.5x8.5', label: '5.5 × 8.5 in' }, { value: '6x9', label: '6 × 9 in' }]
  : [{ value: 'letter', label: 'US Letter' }, { value: 'a4', label: 'A4' }, { value: 'a5', label: 'A5' }];

export type Margins = 'narrow' | 'standard' | 'wide';
export const MARGINS: readonly Margins[] = ['narrow', 'standard', 'wide'];
export const MARGIN_LABELS: Readonly<Record<Margins, string>> = { narrow: 'Narrow', standard: 'Standard', wide: 'Wide' };

/** `FormatSettings.Margins.values(print:)`. */
export function marginValues(m: Margins, print: boolean): Record<string, number> {
  if (!print) return { margins: { narrow: 0.75, standard: 1, wide: 1.25 }[m] };
  switch (m) {
    case 'narrow': return { inside: 0.75, outside: 0.5, top: 0.6, bottom: 0.65 };
    case 'standard': return { inside: 0.875, outside: 0.625, top: 0.7, bottom: 0.75 };
    case 'wide': return { inside: 1, outside: 0.75, top: 0.85, bottom: 0.9 };
  }
}

export const FONTS = ['Times New Roman', 'Palatino', 'Libertinus Serif', 'Courier Prime', 'Arial'];
export const LINE_SPACINGS: Choice<number>[] = [{ value: 1, label: 'Single' }, { value: 1.25, label: '1.25' }, { value: 1.5, label: '1.5' }, { value: 2, label: 'Double' }];
export const PARAGRAPH_GAP = 6;
export const CHAPTER_HEADINGS: string[][] = [
  ['Chapter {numberWords}', '{title}'], ['Chapter {number}', '{title}'], ['{number}', '{title}'], ['{roman}', '{title}'], ['{numberWords}'], ['{title}'],
];
export const PART_HEADINGS: string[][] = [['Part {numberWords}', '{title}'], ['Part {number}', '{title}'], ['Part {roman}', '{title}'], ['{title}']];

/** `FormatSettings.example`: a heading's lines as the book's own section would read. */
export function headingExample(lines: string[], n: number, title: string): string[] {
  return lines.map((l) => l.replaceAll('{numberWords}', numberWords(n)).replaceAll('{number}', String(n)).replaceAll('{roman}', roman(n)).replaceAll('{title}', title));
}

export const CHAPTER_DROPS: Choice<number>[] = [{ value: 1, label: 'Small' }, { value: 1.4, label: 'Medium' }, { value: 2, label: 'Large' }];
export const SCENE_BREAKS: Choice<string>[] = [{ value: '#', label: '#' }, { value: '* * *', label: '* * *' }, { value: '⁂', label: '⁂' }, { value: '', label: 'Blank line' }];
export const END_MARKERS: Choice<string>[] = [{ value: 'END', label: 'END' }, { value: 'The End', label: 'The End' }, { value: '', label: 'Nothing' }];
export const PAGE_EDGE_ORNAMENTS: Choice<string>[] = [{ value: '⁂', label: '⁂' }, { value: '* * *', label: '* * *' }, { value: '#', label: '#' }, { value: '', label: 'Leave it blank' }];

export type Placeholder = 'surname' | 'shortTitle' | 'title' | 'author' | 'page';
export const PLACEHOLDERS: readonly Placeholder[] = ['surname', 'shortTitle', 'title', 'author', 'page'];
export const PLACEHOLDER_LABELS: Readonly<Record<Placeholder, string>> = { surname: 'Surname', shortTitle: 'Short title', title: 'Title', author: 'Author', page: 'Page' };
export const RUNNING_HEADS: Choice<string>[] = [{ value: '{author}', label: 'Author' }, { value: '{title}', label: 'Title' }, { value: '{shortTitle}', label: 'Short title' }, { value: '', label: 'Nothing' }];

export type HeaderPart = { placeholder: Placeholder } | { text: string };
export function headerParts(header: string): HeaderPart[] {
  if (!header) return [];
  return header.split(' / ').map((part) => {
    const p = PLACEHOLDERS.find((x) => `{${x}}` === part);
    return p ? { placeholder: p } : { text: part };
  });
}
export const headerText = (parts: HeaderPart[]) => parts.map((p) => ('placeholder' in p ? `{${p.placeholder}}` : p.text)).join(' / ');

// MARK: FormatAdjustment

export const ADJUSTED_PREFIX = 'adjusted-';
export const adjustedID = (builtInID: string) => ADJUSTED_PREFIX + builtInID;
export const builtInOfAdjusted = (id: string) => (id.startsWith(ADJUSTED_PREFIX) ? id.slice(ADJUSTED_PREFIX.length) : null);
const NAMING = new Set(['name', 'description', 'inherits']);

const isStatusCondition = (item: PValue) =>
  isMap(item) && Object.keys(item).length === 2 && typeof item.field === 'string' && item.field.toLowerCase() === 'status';

/** `FormatAdjustment`: a project's own version of a format; a setting set back to the value it inherits is removed. */
export class FormatAdjustment {
  readonly parentID: string;
  private readonly parent: PMap;

  private constructor(readonly id: string, readonly own: PresetText, parentID: string, parent: PMap) {
    this.parentID = parentID;
    this.parent = parent;
  }

  /** `yaml` null: a new version, named after the parent with the description given. */
  static make(id: string, yaml: string | null, parentID: string, library: PresetLibrary,
    o: { name?: string; description?: string } = {}): FormatAdjustment {
    let own = yaml !== null ? PresetText.read(yaml) : new PresetText();
    const inherits = typeof own.value(['inherits']) === 'string' ? (own.value(['inherits']) as string) : parentID;
    const parent = library.values(inherits);
    if (yaml === null) {
      own = own.set(o.name ?? (typeof parent.name === 'string' ? parent.name : inherits), ['name'])
        .set(o.description ?? 'Adjusted for this project.', ['description'])
        .set(inherits, ['inherits']);
    }
    return new FormatAdjustment(id, own, inherits, parent);
  }

  private with(own: PresetText): FormatAdjustment { return new FormatAdjustment(this.id, own, this.parentID, this.parent); }
  /** The same version with other text (Undo), over the same parent. */
  withYAML(yaml: string): FormatAdjustment { return this.with(PresetText.read(yaml)); }

  get yaml(): string { return this.own.yaml; }
  current(path: readonly string[]): PValue | undefined { return this.own.value(path) ?? valueAt(this.parent, path); }
  inherited(path: readonly string[]): PValue | undefined { return valueAt(this.parent, path); }
  isChanged(s: FormatSetting): boolean { return settingPaths(s).some((p) => this.own.value(p) !== undefined); }
  get changed(): FormatSetting[] { return FORMAT_SETTINGS.filter((s) => this.isChanged(s)); }
  get hasSettings(): boolean { return Object.keys(this.own.tree).some((k) => !NAMING.has(k)); }

  set(value: PValue | undefined, path: readonly string[]): FormatAdjustment {
    if (value === undefined || samePresetValue(value, this.inherited(path))) return this.with(this.own.set(undefined, path));
    return this.with(this.own.set(value, path));
  }

  reset(s: FormatSetting): FormatAdjustment {
    let own = this.own;
    for (const p of settingPaths(s)) own = own.set(undefined, p);
    return this.with(own);
  }

  resetAll(): FormatAdjustment {
    let own = this.own;
    for (const k of Object.keys(own.tree)) if (!NAMING.has(k)) own = own.set(undefined, [k]);
    return this.with(own);
  }

  get excludedStatuses(): string[] {
    const list = this.current(['select', 'exclude']);
    return (Array.isArray(list) ? list : []).flatMap((item) => (isStatusCondition(item) && typeof (item as PMap).equals === 'string' ? [(item as PMap).equals as string] : []));
  }

  setExcludedStatuses(statuses: string[]): FormatAdjustment {
    const list = this.current(['select', 'exclude']);
    const others = (Array.isArray(list) ? list : []).filter((item) => !isStatusCondition(item) || (item as PMap).equals === undefined);
    return this.set([...others, ...statuses.map((s) => ({ field: 'status', equals: s }))], ['select', 'exclude']);
  }

  get onlyCollection(): string | null {
    const list = this.current(['select', 'include']);
    if (!Array.isArray(list) || list.length !== 1 || !isMap(list[0]) || Object.keys(list[0]).length !== 1) return null;
    return typeof list[0].collection === 'string' ? list[0].collection : null;
  }

  setOnlyCollection(name: string | null): FormatAdjustment {
    return this.set(name === null ? undefined : [{ collection: name }], ['select', 'include']);
  }

  margins(print: boolean): Margins | null {
    return MARGINS.find((m) => Object.entries(marginValues(m, print)).every(([k, v]) => {
      const now = this.current(['render', 'page', k]) ?? (print ? this.current(['render', 'page', 'margins']) : undefined);
      return samePresetValue(now, v);
    })) ?? null;
  }

  setMargins(m: Margins, print: boolean): FormatAdjustment {
    let own = this.own;
    for (const k of ['margins', 'inside', 'outside', 'top', 'bottom']) own = own.set(undefined, ['render', 'page', k]);
    let next = this.with(own);
    for (const [k, v] of Object.entries(marginValues(m, print))) next = next.set(v, ['render', 'page', k]);
    return next;
  }

  /** Indented paragraphs (the indent, in inches), or null: space between them. */
  get indent(): number | null {
    const v = this.current(['render', 'firstLineIndent']);
    const n = typeof v === 'number' ? v : 0;
    return n > 0 ? n : null;
  }

  setIndent(inches: number | null): FormatAdjustment {
    return inches !== null
      ? this.set(inches, ['render', 'firstLineIndent']).set(0, ['render', 'paragraphSpacing'])
      : this.set(0, ['render', 'firstLineIndent']).set(PARAGRAPH_GAP, ['render', 'paragraphSpacing']);
  }

  indentParagraphs(manuscript: boolean): FormatAdjustment {
    const v = this.inherited(['render', 'firstLineIndent']);
    const n = typeof v === 'number' ? v : 0;
    return this.setIndent(n > 0 ? n : manuscript ? 0.5 : 0.25);
  }
}

// MARK: The list

/** `PublishFormat.adjusted`: the project's adjusted versions in place of the built-ins they adjust. */
export function adjustedFormats(formats: PublishFormat[], own: Iterable<string>): PublishFormat[] {
  const mine = new Set(own);
  return formats.map((f) => ({
    ...f,
    variants: f.variants.map((v) => (mine.has(adjustedID(v.presetID)) ? { ...v, presetID: adjustedID(v.presetID) } : v)),
  }));
}

export const isAdjustedPreset = (id: string) => builtInOfAdjusted(id) !== null;
